import { colorTokens, scaleTokens } from './semantic';

const {
  content,
  surface,
  border,
  icon,
  status,
  accent,
  shadow: shadowColor,
} = colorTokens;
const { space, radius, fontSize, fontWeight, lineHeight, fontFamily, shadow } =
  scaleTokens;

export const componentTokens = {
  globalStyle: {
    fontFamily: fontFamily.sansFace,
    codeFontFamily: fontFamily.mono,
    scrollbarThumb: surface.scrollbar,
  },
  typography: {
    color: content.primary,
    fontSize: fontSize.base,
    headingWeight: fontWeight.bold,
    headingSize: {
      h3: fontSize.xl,
      h4: fontSize.displayLg,
      h5: fontSize.xxl,
      h6: fontSize.lg,
    },
    body1: {
      color: content.secondary,
      fontSize: fontSize.base,
      fontWeight: fontWeight.regular,
    },
    caption: {
      color: content.placeholder,
      fontSize: fontSize.base,
      lineHeight: lineHeight.relaxed,
    },
  },
  formControl: {
    label: content.placeholder,
    labelFocused: content.primary,
    labelFontSize: fontSize.base,
    labelPaddingRight: space.xs,
    labelPaddingTop: space.hairline,
  },
  inputLabel: {
    color: content.primary,
  },
  input: {
    radius: radius.lg,
  },
  menu: {
    border: border.light,
    radius: radius.lg,
    shadow: shadow.md,
    item: {
      padding: `${space.smMinus} ${space.lg}`,
      paddingSm: space.sm,
      paddingLg: space.lg,
      borderRadius: radius.lg,
      hoverBackground: surface.tinted,
      selectedBackground: surface.sunken,
      selectedHoverBackground: surface.sunken,
      fontWeight: fontWeight.regular,
    },
    title: {
      fontSize: fontSize.sm,
      color: content.disabled,
      padding: `${space.sm} ${space.lg}`,
    },
    optionDescription: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    clearItem: {
      margin: `0 ${space.xs} ${space.sm}`,
      fontSize: fontSize.base,
      color: accent.link,
    },
    options: {
      gap: space.sm,
      margin: `0 ${space.xs}`,
    },
  },
  autocomplete: {
    border: border.subtle,
    radius: radius.md,
    shadow: shadow.md,
    textField: {
      endAdornmentMarginLeft: space.xs,
      disabledBackground: surface.muted,
    },
    wrapper: {
      disabledBackground: surface.muted,
      inputPadding: space.xs,
    },
    tagChip: {
      color: content.primary,
    },
    editInput: {
      fontSize: fontSize.base,
      padding: `0 ${space.xs}`,
      color: content.primary,
    },
    suggestionList: {
      padding: space.xs,
    },
    suggestionItem: {
      fontSize: fontSize.base,
      padding: `${space.mdMinus} ${space.lg}`,
      borderRadius: radius.lg,
      backgroundColor: surface.translucent.hover,
      hoverBackgroundColor: surface.translucent.hover,
    },
    noOptions: {
      fontSize: fontSize.base,
      padding: `${space.mdPlus} ${space.lg}`,
      color: content.translucent.secondary,
    },
    actionItem: {
      fontSize: fontSize.base,
      fontWeight: fontWeight.semibold,
      color: accent.link,
      padding: `${space.mdMinus} ${space.lg}`,
      borderRadius: radius.lg,
      background: surface.base,
      hoverBackgroundColor: surface.tinted,
    },
    invalidChipWrapper: {
      textDecorationColor: status.error.main,
      padding: `0 ${space.sm}`,
    },
    paper: {
      padding: space.xs,
      borderBottom: border.subtle,
      margin: `${space.xs} 0`,
    },
    renderOption: {
      color: content.tertiary,
    },
    listboxTitle: {
      padding: `${space.smMinus} ${space.lg}`,
      fontSize: fontSize.sm,
      fontWeight: fontWeight.medium,
      color: content.tertiary,
    },
    listbox: {
      padding: space.xs,
    },
    option: {
      fontSize: fontSize.base,
      padding: `${space.mdMinus} ${space.lg}`,
      borderRadius: radius.lg,
      background: surface.base,
      hoverBackground: surface.tinted,
    },
    groupLabel: {
      fontSize: fontSize.sm,
      fontWeight: fontWeight.semibold,
      color: content.disabled,
      padding: `${space.sm} ${space.lg} ${space.xs}`,
    },
  },
  dialog: {
    border: border.subtle,
    radius: radius.lg,
    backdrop: surface.backdrop,
    title: {
      gap: space.sm,
      padding: `${space.md} ${space.lg}`,
      backgroundColor: surface.sunken,
      fontWeight: fontWeight.semibold,
    },
    message: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    content: {
      gap: space.lg,
      padding: `${space.xl} ${space.xxl}`,
    },
    buttonContainer: {
      gap: space.lg,
      lineHeight: '42px',
    },
    footer: {
      gap: space.lg,
      borderTop: border.subtle,
      padding: `${space.lg} ${space.xxl}`,
    },
    button: {
      marginLeft: '30px',
    },
  },
  drawer: {
    footerHeight: '72px',
    title: {
      fontSize: fontSize.lg,
      fontWeight: fontWeight.semibold,
    },
    paper: {
      shadow: shadow.md,
      radius: `${radius.lg} 0 0 ${radius.lg}`,
    },
    wrapper: {
      backgroundColor: surface.base,
      borderRadius: `${radius.lg} 0 0 ${radius.lg}`,
      border: border.divider,
    },
    header: {
      padding: `${space.lg} ${space.xxl}`,
      borderBottom: border.divider,
    },
    footer: {
      padding: `${space.lg} ${space.xxl}`,
      borderTop: border.divider,
    },
    content: {
      padding: space.xxl,
    },
    closeIcon: {
      fill: icon.primary,
    },
    headerLeft: {
      gap: space.lg,
    },
    headerRight: {
      gap: space.md,
    },
    backArrowButton: {
      fill: icon.secondary,
      disabledFill: icon.primary,
    },
  },
  divider: {
    color: border.divider,
    lightColor: border.subtle,
  },
  tooltip: {
    bg: surface.inverse,
    radius: radius.lg,
    padding: space.sm,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.regular,
    maxWidth: '400px',
    informationIcon: {
      pathFill: icon.disabled,
      hoverPathFill: icon.primary,
    },
  },
  alert: {
    radius: radius.lg,
    fg: content.primary,
    successBg: status.success.bg,
    errorBg: status.error.bg,
    warningBg: status.warning.bg,
    attentionBg: status.attention.bg,
    infoBg: status.info.bg,
    iconSize: '32px',
    standardInfo: {
      background: status.info.bgStrong,
      iconFill: accent.secondary,
    },
    title: {
      marginBottom: space.xs,
    },
  },
  accordion: {
    summaryDivider: border.dividerSubtle,
  },
  aiChat: {
    askGradient: {
      start: border.highlight,
      end: accent.secondary,
    },
    submitHoverBg: surface.hover,
    messageBorder: border.highlight,
    lineageToolConnector: border.control,
    tableToolFade: surface.fade,
  },
  avatar: {
    extraSmallFontSize: '8px',
  },
  fillBar: {
    trackStroke: surface.sunken,
  },
  floatingActionsMenu: {
    radius: '24px',
    bg: surface.translucent.frostedGray,
    dropShadow: `drop-shadow(0px 8px 24px ${shadowColor.medium})`,
    itemHoverBg: surface.translucent.subtle,
  },
  incrementSelector: {
    border: border.tinted,
  },
  navbar: {
    expandedShadow: `4px 0 16px -4px ${shadowColor.soft}, 8px 0 32px -8px ${shadowColor.light}`,
  },
  lineage: {
    minimapBorder: border.mediumStrong,
    greyedOutStroke: border.light,
    infoPanelGripBorder: border.mediumSoft,
    category: {
      source: { border: accent.secondaryMuted, background: surface.highlight },
      exposure: { border: accent.tertiary, background: accent.tertiarySubtle },
      semantic: {
        border: status.success.bright,
        background: status.success.bgBright,
      },
      failing: { border: status.error.soft, background: status.error.bg },
      warning: { border: status.warning.strong, background: status.warning.bg },
      passing: { border: status.success.main, background: status.success.bg },
      neutral: { border: border.medium, background: surface.base },
    },
    contextMenu: {
      border: border.faint,
      background: surface.muted,
    },
  },
  checkbox: {
    border: icon.subtle,
    uncheckedBg: surface.base,
    checkedBg: surface.inverse,
    mark: icon.inverse,
  },
  radio: {
    ring: icon.subtle,
    checked: icon.primary,
  },
  button: {
    radius: radius.lg,
    disabledIcon: icon.disabled,
    loadingStartPadding: space.huge,
    size: {
      extraSmall: {
        height: '28px',
        padding: `${space.xs} ${space.sm}`,
        fontSize: fontSize.sm,
        fontWeight: fontWeight.medium,
      },
      medium: {
        height: '32px',
        padding: `${space.smMinus} ${space.mdMinus}`,
        fontSize: fontSize.base,
        fontWeight: fontWeight.medium,
      },
      large: {
        height: '40px',
        padding: `${space.mdMinus} ${space.lg}`,
        fontSize: fontSize.lg,
        fontWeight: fontWeight.semibold,
      },
    },
    contained: {
      primary: {
        bg: accent.primary,
        fg: content.inverse,
        disabledBg: surface.hover,
        disabledFg: content.disabled,
      },
      secondary: {
        bg: surface.base,
        fg: content.primary,
        hoverBg: surface.hover,
        disabledBg: surface.base,
        disabledFg: content.disabled,
      },
      error: {
        bg: status.error.main,
        fg: content.inverse,
        hoverBg: status.error.main,
      },
    },
    outlined: {
      primary: {
        bg: surface.base,
        border: border.control,
        fg: content.primary,
        hoverBg: surface.hover,
        hoverBorder: border.control,
        disabledBg: surface.muted,
        disabledFg: content.disabled,
      },
      secondary: {
        bg: surface.subtle,
        border: border.control,
        fg: content.primary,
        hoverBg: surface.subtle,
        hoverBorder: border.strong,
      },
    },
    text: {
      secondary: {
        fg: content.primary,
      },
    },
  },
  chip: {
    radius: radius.sm,
    roundedRadius: radius.xxl,
    fontSize: fontSize.base,
    fontWeight: fontWeight.regular,
    gap: space.xs,
    padding: `${space.xs} ${space.smMinus}`,
    deletablePadding: `${space.xs} ${space.xs} ${space.smMinus} ${space.smMinus}`,
    roundedPadding: `${space.xs} ${space.mdMinus} ${space.smMinus} ${space.smMinus}`,
    roundedDeletablePadding: `${space.xs} ${space.smMinus} ${space.smMinus} ${space.smMinus}`,
    roundedLabelPaddingLeft: space.xxsPlus,
    deletableLabelPaddingRight: space.md,
    deleteIconHitAreaRadius: `0 ${radius.sm} ${radius.sm} 0`,
    deleteIconHoverBg: surface.translucent.hover,
    dashedBorder: border.medium,
    smallFontSize: fontSize.xs,
    mediumFontSize: fontSize.xs,
    colors: {
      error: { fg: status.error.bright, bg: status.error.bgStrong },
      warning: { fg: status.warning.main, bg: status.warning.bgStrong },
      success: { fg: status.success.bright, bg: status.success.bgBright },
      idle: { fg: accent.secondary, bg: surface.highlight },
      merged: { fg: accent.tertiary, bg: accent.tertiarySubtle },
      primary: { fg: accent.secondary, bg: surface.highlight },
      default: {
        fg: content.secondary,
        bg: surface.sunken,
        customVariantBg: surface.base,
      },
    },
    quickFilter: {
      padding: `${space.xs} ${space.md}`,
      marginRight: space.sm,
      color: content.primary,
      background: surface.base,
      hoverBackground: surface.sunken,
      selectedColor: content.inverse,
      selectedBackground: surface.inverse,
      selectedHoverBackground: surface.inverse,
    },
    editable: {
      backgroundColor: surface.base,
      borderRadius: radius.xs,
      border: border.default,
      clickableHoverBackgroundColor: surface.muted,
      hoverBackgroundColor: surface.base,
    },
    chipContent: {
      gap: space.smMinus,
    },
  },
  iconButton: {
    root: {
      borderRadius: radius.lg,
      padding: space.xs,
      border: border.medium,
    },
  },
  splitButton: {
    caretButton: {
      marginLeft: space.hairline,
      outlinedBorderLeft: border.control,
      containedBorderLeft: border.controlStrong,
    },
    root: {
      fontSize: fontSize.xxl,
    },
  },
  toggleButton: {
    root: {
      paddingTop: space.smMinus,
      paddingBottom: space.smMinus,
      color: content.secondary,
      borderColor: border.controlStrong,
      borderRadius: radius.lg,
      selectedBackground: surface.sunken,
      selectedColor: content.secondary,
      hoverColor: status.error.bright,
      hoverBackground: status.error.bgStrong,
      warningHoverColor: status.warning.main,
      warningHoverBackground: status.warning.bgStrong,
    },
  },
  segmentedButton: {
    toggleIconButton: {
      gap: space.sm,
      color: content.secondary,
      borderRadius: radius.lg,
      smallPadding: space.xs,
      mediumPadding: space.sm,
      largePadding: space.md,
      hoverBorder: status.error.bright,
      selectedColor: content.secondary,
      selectedBorder: status.error.bright,
      selectedShadow: `0 1px 3px 0 ${shadowColor.solid}, 0 4px 8px 3px ${shadowColor.solidSoft}`,
      selectedShadowSmall: `0 1px 2px 0 ${shadowColor.solid}, 0 2px 4px 2px ${shadowColor.solidSoft}`,
      selectedShadowMedium: `0 1px 3px 0 ${shadowColor.solid}, 0 3px 6px 2px ${shadowColor.solidSoft}`,
    },
    toggleButtonGroup: {
      gap: space.sm,
      groupedBorder: border.controlStrong,
      groupedBorderRadius: radius.lg,
    },
  },
  table: {
    columnActions: {
      gap: space.md,
    },
    headerCell: {
      paddingBottom: space.md,
      selectionPaddingBottom: space.xxs,
      fontSize: fontSize.md,
      fontWeight: fontWeight.regular,
    },
    tableCell: {
      borderTop: border.muted,
      paddingRight: space.sm,
      fontSize: fontSize.base,
      expandedContentPadding: space.lg,
      expandedContentMarginBottom: space.xs,
      headSelectionPaddingBottom: space.md,
    },
    sortArrow: {
      fill: icon.secondary,
      hoverFill: icon.primary,
      activeFill: accent.link,
      activeHoverFill: accent.selected,
    },
    clickableTableRow: {
      selectedBackground: surface.muted,
      stickyBackground: surface.base,
      hoverBackground: surface.subtle,
      blinkBackground: surface.divider,
    },
    expandButtonCell: {
      svgColor: content.secondary,
      svgMarginTop: space.mdPlus,
      svgMarginLeft: space.xl,
    },
    skeletonRow: {
      borderTop: border.muted,
      gap: space.massive,
    },
    skeletonCell: {
      borderRadius: radius.sm,
    },
    headerCellContent: {
      gap: space.lg,
    },
    resizer: {
      handleBackground: surface.track,
      handleRadius: radius.sm,
      handleMarginTop: space.xxs,
      handleHoverBackground: surface.trackStrong,
      handleActiveBackground: surface.trackStrong,
    },
    groupHeaderRow: {
      backgroundColor: surface.base,
      hoverBackgroundColor: surface.subtle,
    },
    groupHeaderCell: {
      borderTop: border.muted,
      borderBottom: border.muted,
      padding: `${space.md} ${space.lg}`,
      compactPadding: `${space.smMinus} ${space.lg}`,
      fontSize: fontSize.base,
    },
    groupLoadingCell: {
      padding: `${space.md} ${space.lg}`,
    },
    floatingStickyGroupRow: {
      backgroundColor: surface.muted,
      hoverBackgroundColor: surface.subtle,
    },
    groupHeaderLeft: {
      gap: space.mdMinus,
      marginLeft: space.xxs,
    },
    groupExpandIcon: {
      color: content.secondary,
    },
    groupTitle: {
      color: content.primary,
    },
    groupCount: {
      fontWeight: fontWeight.regular,
      color: content.tertiary,
      marginLeft: space.xs,
    },
    groupMetadata: {
      gap: space.lg,
    },
    groupedTable: {
      color: content.secondary,
    },
    wrapper: {
      borderRadius: radius.lg,
      border: border.muted,
      backgroundColor: surface.subtle,
      defaultPadding: '14px 30px',
    },
    root: {
      cellBorder: border.muted,
      cellPadding: `${space.mdMinus} ${space.xxl}`,
      cellFontWeight: fontWeight.regular,
      cellFontSize: fontSize.md,
      headerColor: content.secondary,
    },
    stickyCell: {
      background: surface.base,
      dividerColor: border.muted,
      dividerShadowColor: border.strong,
    },
    tableHead: {
      background: surface.base,
    },
  },
  switch: {
    root: {
      switchBaseMargin: space.xxs,
      switchBaseCheckedColor: content.inverse,
      checkedTrackBackgroundColor: surface.inverse,
      focusVisibleThumbColor: content.primary,
      disabledThumbColor: content.inverse,
      trackBackgroundColor: surface.trackStrong,
      focusVisibleThumbBorder: border.inverse,
      trackBorderRadius: '13px',
    },
  },
  textField: {
    input: {
      fontSize: fontSize.base,
      radius: radius.lg,
      disabledBackground: surface.muted,
      disabledColor: content.placeholder,
      border: border.control,
      outlineRadius: radius.lg,
      focusedOutline: border.strong,
      hoverOutline: border.strong,
      focusedBorder: border.strong,
      labelShrinkFontSize: fontSize.base,
      placeholderColor: content.subtle,
      errorBorder: status.error.bright,
      autofillShadow: `0 0 0 30px ${surface.base} inset`,
    },
  },
  dropDown: {
    select: {
      borderRadius: radius.lg,
      borderColor: border.strong,
      color: content.primary,
      fontSize: fontSize.base,
      border: border.control,
      focusedBorder: border.strong,
      hoverBorder: border.strong,
      errorBorder: status.error.bright,
      errorHoverBorder: status.error.bright,
      disabledBackground: surface.muted,
      disabledTextColor: content.disabled,
      valueFontSize: fontSize.base,
      disabledBorder: border.disabled,
      background: surface.base,
    },
    searchFieldContainer: {
      borderBottom: border.light,
      background: surface.base,
    },
    searchIconFocused: {
      pathFill: icon.primary,
    },
    divider: {
      background: surface.hover,
      marginBottom: space.sm,
    },
    menuPaper: {
      borderRadius: radius.lg,
      borderColor: border.light,
    },
    root: {
      color: content.tertiary,
      gap: space.sm,
      fontSize: fontSize.sm,
    },
    counter: {
      fontSize: fontSize.sm,
      color: content.secondary,
    },
    options: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    menuItem: {
      padding: space.sm,
      paddingLg: space.lg,
      borderRadius: radius.lg,
      fontSize: fontSize.base,
      hoverBackground: surface.tinted,
      selectedHoverBackground: surface.tinted,
    },
    label: {
      shrinkFontSize: fontSize.base,
      shrinkPadding: `0 ${space.xs}`,
      background: surface.base,
    },
  },
  filterChip: {
    grayText: {
      color: content.muted,
      fontSize: fontSize.md,
    },
    wrapper: {
      backgroundColor: surface.tinted,
      border: border.selected,
      borderRadius: radius.lg,
      padding: `0 ${space.md}`,
      deletablePadding: `0 ${space.xs} 0 ${space.md}`,
      gap: space.lg,
    },
    closeButtonWrapper: {
      borderLeft: border.selected,
      paddingLeft: space.xs,
    },
    iconButton: {
      padding: space.smMinus,
    },
    filterValueText: {
      fontSize: fontSize.md,
    },
    flexGap: {
      gap: space.xs,
    },
    tooltipContentWrapper: {
      padding: space.xs,
    },
    filterValueSection: {
      gap: space.xs,
    },
    root: {
      fontSize: fontSize.md,
    },
    searchFieldContainer: {
      borderBottom: border.light,
      background: surface.base,
    },
    searchIconFocused: {
      pathFill: icon.primary,
    },
    selectedSectionHeader: {
      fontSize: fontSize.sm,
      color: content.tertiary,
      padding: `${space.sm} ${space.md} ${space.xs}`,
    },
    showMoreButton: {
      fontSize: fontSize.sm,
      color: accent.primary,
      padding: `${space.sm} ${space.md}`,
    },
    divider: {
      background: surface.hover,
      margin: `${space.sm} 0`,
    },
    noOptionsText: {
      fontSize: fontSize.sm,
      padding: space.sm,
    },
    menuItemWrapper: {
      paddingLeft: space.xs,
      paddingRight: space.xs,
    },
    optionContentContainer: {
      gap: space.sm,
      paddingRight: space.sm,
    },
    counterText: {
      fontSize: fontSize.sm,
      color: content.secondary,
    },
    optionItem: {
      fontSize: fontSize.sm,
      color: content.tertiary,
    },
    selectAllMenuItem: {
      marginTop: space.xs,
      marginBottom: space.sm,
      gap: space.sm,
    },
    selectAllText: {
      paddingRight: space.sm,
    },
  },
  snackbar: {
    message: {
      successColor: status.success.main,
      warningColor: status.warning.strong,
      errorColor: status.error.soft,
      infoColor: accent.primary,
    },
    actionButton: {
      color: content.inverse,
    },
  },
};
